/* TRAPT v0.9.5 — first-slide-first, atomic slideshow readiness */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const initHero = hero => {
    if (hero.dataset.t94Ready) return;
    hero.dataset.t94Ready = 'true';
    const slides = [...hero.querySelectorAll('[data-t94-slide]')];
    const dots = [...hero.querySelectorAll('[data-t94-dot]')];
    const images = [...hero.querySelectorAll('[data-t94-slide] img')];
    const duration = Math.max(3000, Number(hero.dataset.duration) || 6000);
    const canAutoplay = hero.dataset.autoplay === 'true' && slides.length > 1 && !reducedMotion.matches;
    let active = Math.max(0, slides.findIndex(slide => slide.classList.contains('is-active')));
    let timer = 0;
    let paused = false;
    let pointerStart = null;
    let ready = slides.length <= 1;
    let readinessRun = 0;
    hero.style.setProperty('--t94-duration', `${duration}ms`);

    const syncTone = () => {
      const tone = slides[active]?.dataset.slideTone ||
        (slides[active]?.classList.contains('t94-hero__slide--light') ? 'light' : 'dark');
      hero.dataset.activeTone = tone;
    };

    const stop = () => {
      window.clearTimeout(timer);
      timer = 0;
      hero.classList.remove('is-autoplaying');
    };
    const schedule = () => {
      stop();
      if (!ready || !canAutoplay || paused || document.hidden) return;
      hero.classList.add('is-autoplaying');
      timer = window.setTimeout(() => show((active + 1) % slides.length, true), duration);
    };
    const show = (next, restart = true) => {
      if (!slides.length || !ready) return;
      active = (next + slides.length) % slides.length;
      slides.forEach((slide, index) => {
        const selected = index === active;
        slide.classList.toggle('is-active', selected);
        slide.setAttribute('aria-hidden', String(!selected));
      });
      dots.forEach((dot, index) => {
        const selected = index === active;
        dot.classList.toggle('is-active', selected);
        dot.setAttribute('aria-selected', String(selected));
      });
      syncTone();
      if (restart) schedule();
    };
    const settleImage = image => {
      image.loading = 'eager';
      const loaded = image.complete
        ? Promise.resolve(image.naturalWidth > 0)
        : new Promise(resolve => {
            image.addEventListener('load', () => resolve(true), { once: true });
            image.addEventListener('error', () => resolve(false), { once: true });
          });
      return loaded.then(success => {
        if (!success || typeof image.decode !== 'function') return success;
        return image.decode().then(() => true).catch(() => image.naturalWidth > 0);
      });
    };

    dots.forEach(dot => dot.addEventListener('click', () => show(Number(dot.dataset.slideIndex) || 0)));
    // Pause only over the copy and the controls. Hovering anywhere on a full-bleed hero
    // (which is most of the screen, and always true inside the theme editor) made the
    // slideshow look like it had stopped.
    const pauseZone = hero.querySelector('.t94-hero__copy, .t4-hero__copy') || null;
    const pauseTargets = [pauseZone, hero.querySelector('.t94-hero__dots, [data-t94-dots]')].filter(Boolean);
    pauseTargets.forEach((zone) => {
      zone.addEventListener('mouseenter', () => { paused = true; stop(); });
      zone.addEventListener('mouseleave', () => { paused = false; schedule(); });
    });
    hero.addEventListener('focusin', () => { paused = true; stop(); });
    hero.addEventListener('focusout', event => {
      if (hero.contains(event.relatedTarget)) return;
      paused = false;
      schedule();
    });
    hero.addEventListener('keydown', event => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      show(active + (event.key === 'ArrowRight' ? 1 : -1));
    });
    hero.addEventListener('pointerdown', event => {
      if (event.pointerType === 'mouse') return;
      pointerStart = { x: event.clientX, y: event.clientY };
    }, { passive: true });
    hero.addEventListener('pointerup', event => {
      if (!pointerStart) return;
      const dx = event.clientX - pointerStart.x;
      const dy = event.clientY - pointerStart.y;
      pointerStart = null;
      if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy)) return;
      show(active + (dx < 0 ? 1 : -1));
    }, { passive: true });

    const visibilityHandler = () => document.hidden ? stop() : schedule();
    document.addEventListener('visibilitychange', visibilityHandler);
    const checkReadiness = () => {
      const run = ++readinessRun;
      ready = slides.length <= 1;
      stop();
      hero.classList.remove('is-media-ready', 'has-media-error');
      const first = images[0] ? settleImage(images[0]) : Promise.resolve(true);
      first.then(firstReady => {
        if (run !== readinessRun) return [];
        hero.classList.toggle('is-first-ready', firstReady);
        return Promise.all(images.slice(1).map(settleImage)).then(results => [firstReady, ...results]);
      }).then(results => {
        if (run !== readinessRun || !results.length) return;
        ready = results.every(Boolean);
        hero.classList.add('is-media-ready');
        hero.classList.toggle('has-media-error', !ready);
        if (ready) schedule();
      });
    };
    const sourceQuery = window.matchMedia('(max-width: 760px)');
    const sourceChangeHandler = () => window.requestAnimationFrame(checkReadiness);
    sourceQuery.addEventListener?.('change', sourceChangeHandler);
    syncTone();
    checkReadiness();
    document.addEventListener('shopify:section:unload', event => {
      if (!event.target.contains(hero)) return;
      stop();
      document.removeEventListener('visibilitychange', visibilityHandler);
      sourceQuery.removeEventListener?.('change', sourceChangeHandler);
    });
  };
  const init = root => root.querySelectorAll('[data-t94-hero]').forEach(initHero);
  document.addEventListener('DOMContentLoaded', () => init(document));
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
